import type { ReactNode, Ref } from 'react'
import { Link } from 'react-router'

import { PageHeader } from '../PageHeader'
import { Card, Pill } from '../ui'
import type { ComparisonResult, RecordingRecord } from '../../lib/api'
import { experimentDate, formatLocalClock } from '../../lib/format'
import type { HomeDisplayView } from '../../lib/home'
import { ComparisonJournalColumns } from './ComparisonJournalColumns'
import { ComparisonMap } from './ComparisonMap'
import { ComparisonRows } from './ComparisonRows'
import { comparisonMapIsDrawable } from '../../lib/stateGeometry'
import type { ComparisonScoreLabels } from './ComparisonLabels'

/** Payload words are rendered verbatim; the short labels are the boards' fixed copy. */
export function ComparisonContent({ result, before, after, display, headingRef, scoreLabels = {}, assistantAffordance }: {
  result: ComparisonResult
  before: RecordingRecord
  after: RecordingRecord
  display: HomeDisplayView | null
  headingRef?: Ref<HTMLElement>
  scoreLabels?: ComparisonScoreLabels
  assistantAffordance?: ReactNode
}) {
  const beforeTime = formatLocalClock(before.start_local_time)
  const afterTime = formatLocalClock(after.start_local_time)
  const comparable = result.category === 'comparable'
  const mapPresent = comparisonMapIsDrawable(result, display)
  const date = before.local_date ?? before.session_date
  const validity = result.validity.filter((check) => check.text.trim() !== '')
  const warnings = validity.filter((check) => check.passed === false)
  const checks = validity.filter((check) => check.passed !== false)
  return <div className="stack stack--wide comparison" data-testid="comparison-screen">
    <PageHeader ref={headingRef} tabIndex={-1} title={`${beforeTime} → ${afterTime}`}
      eyebrow={`History · ${experimentDate(date)} · Compare`}
      back={<Link className="page__back" to="/history">← History</Link>}
      lead={result.text.trim() ? result.text : undefined}>{assistantAffordance}</PageHeader>

    <div className="comparison__chips" role="group" aria-label="Comparability">
      {comparable && <Pill tone="green" dot>Comparable</Pill>}
      {warnings.map((check) => <Pill key={check.check}
        tone={check.passed === false ? 'amber' : 'neutral'} dot={check.passed === false}
        data-reason={check.reason ?? undefined}>{check.text}</Pill>)}
      {result.reasons.filter((reason) => !validity.some((check) => check.reason === reason)).map((reason) =>
        <Pill key={reason} tone="amber" dot data-reason={reason}>{reason}</Pill>)}
      {result.context.refit === true && <Pill tone="amber" dot>Refit between readings</Pill>}
      {Boolean(checks.length) && <details className="comparison__checks">
        <summary>Comparison checks</summary>
        <ul>{checks.map((check) => <li key={check.check}>{check.text}</li>)}</ul>
      </details>}
    </div>

    {comparable ? <div className={`comparison__readings${mapPresent ? '' : ' comparison__readings--no-map'}`}>
      <ComparisonMap result={result} beforeTime={beforeTime} afterTime={afterTime} display={display} />
      <ComparisonRows result={result} beforeTime={beforeTime} afterTime={afterTime} scoreLabels={scoreLabels} />
    </div> : <Card as="section" className="comparison__refusal" aria-label="Comparison unavailable">
      {result.text.trim() && <p>{result.text}</p>}
      {result.next_step_text.trim() && <p className="comparison__muted">{result.next_step_text}</p>}
      <div className="comparison__open">
        <Link className="btn btn--secondary" to={`/history/${encodeURIComponent(before.recording_id)}`}>Open {beforeTime}</Link>
        <Link className="btn btn--secondary" to={`/history/${encodeURIComponent(after.recording_id)}`}>Open {afterTime}</Link>
      </div>
    </Card>}

    <div className="comparison__foot">
      <ComparisonJournalColumns before={result.context.journal_before} after={result.context.journal_after}
        beforeTime={beforeTime} afterTime={afterTime} beforeId={before.recording_id} afterId={after.recording_id} />
      <Card as="section" flat className="comparison__meaning" aria-label="What this is, and is not">
        <h2 className="comparison__label">What this is, and is not</h2>
        {result.text.trim() && <p>{result.text}</p>}
        {result.limitation_text.trim() && <p>{result.limitation_text}</p>}
        {result.pattern_text.trim() && <p>{result.pattern_text}</p>}
        <p>Nothing here changes your baseline.</p>
      </Card>
    </div>
  </div>
}
